import React, { Fragment } from 'react';
import { StyleSheet, StatusBar } from 'react-native';
import { useRoute } from '@react-navigation/native';

import i18n from 'i18n-js';
import { useTheme } from 'react-native-paper';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { LabelPosition } from '@react-navigation/bottom-tabs/lib/typescript/src/types';

import { TabBarIcon } from '../components/icon';
import { Text } from '../components/themed-view';
import { useIsDark } from '../hooks/theme';

import ExplorerScreen from '../screens/explorer';
import OracleScreen from '../screens/oracle';
import SettingsScreen from '../screens/settings';
import Filter, { filtered } from '../constants/filter';
import { hexagrams } from '../constants/hexagram';
import { RootParamList } from '../types';
import useTranslation from '../hooks/translation';

type Props = {
  focused: any;
  color: any;
  // eslint-disable-next-line react/require-default-props, react/no-unused-prop-types
  position?: LabelPosition;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const BarLabel = ({ focused, color, children }: Props & { children: any }) => {
  // @todo useTheme, const { colors } = useTheme();
  return <Text style={[styles.text, { color }]}>{children}</Text>;
};

BarLabel.defaultProps = {
  position: 'below-icon'
};

const Tab = createBottomTabNavigator();

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function BottomBarNavigation() {
  const route = useRoute();
  const params = route.params as RootParamList;
  const { colors } = useTheme();
  const barStyle = useIsDark() ? 'light-content' : 'dark-content';

  useTranslation();

  const badge = (f?: Filter) => {
    const count = filtered(
      hexagrams,
      f || (params && params.filter) || undefined
    ).length;
    // @todo, cache filtered to prevent calculating twice
    return count === 64 || !count ? undefined : count;
  };

  return (
    <>
      <StatusBar
        barStyle={barStyle}
        backgroundColor={colors.surface}
        animated
      />
      <Tab.Navigator
        initialRouteName="Explorer"
        tabBarOptions={{
          adaptive: false // @todo adaptive TabBarIcon styling
        }}
      >
        <Tab.Screen
          name="Explorer"
          component={ExplorerScreen}
          options={{
            tabBarIcon: (props: { color: string }) => (
              // eslint-disable-next-line react/destructuring-assignment
              <TabBarIcon name="book-open-variant" color={props.color} />
            ),
            tabBarLabel: (props: Props) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <BarLabel {...props}>{i18n.t('Explorer')}</BarLabel>
            ),
            title: i18n.t('Explorer'),
            tabBarBadge: badge(),
            tabBarBadgeStyle: styles.badge
          }}
        />
        <Tab.Screen
          name="Oracle"
          component={OracleScreen}
          options={{
            tabBarIcon: (props: { color: string }) => (
              // eslint-disable-next-line react/destructuring-assignment
              <TabBarIcon name="chat-processing-outline" color={props.color} />
            ),
            tabBarLabel: (props: Props) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <BarLabel {...props}>{i18n.t('Oracle')}</BarLabel>
            ),
            title: i18n.t('Oracle')
          }}
        />
        <Tab.Screen
          name="Settings"
          component={SettingsScreen}
          options={{
            tabBarIcon: (props: { color: string }) => (
              // eslint-disable-next-line react/destructuring-assignment
              <TabBarIcon name="tools" color={props.color} />
            ),
            tabBarLabel: (props: Props): JSX.Element => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <BarLabel {...props}>{i18n.t('Settings')}</BarLabel>
            ),
            title: i18n.t('Settings')
          }}
        />
      </Tab.Navigator>
    </>
  );
}

const styles = StyleSheet.create({
  text: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 5
  },
  badge: {
    fontSize: 9,
    paddingBottom: 5
  }
});

export default BottomBarNavigation;
